// i18next-extract-mark-ns-start Partners
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import CTALink from '../../CTALink';
import { navigate } from '../../../services/navigation';
import withAppContext from '../../../withAppContext';

import {
  tabletThresholdPixels, Button, frames, Title, colors, margins,
  mobileThresholdPixels,
} from './styledComponents';
import taraJarmonLogo from '../../../assets/partners/taraJarmon.png';
import logoSoeur from '../../../assets/partners/logo_soeur.png';
import logo5ASec from '../../../assets/partners/5ASec.png';
import maduraLogo from '../../../assets/partners/madura.png';
import asphalteLogo from '../../../assets/partners/Asphalte.png';
import mariaDeLaOrdenLogo from '../../../assets/partners/mariaDeLaOrden.png';
import faguoLogo from '../../../assets/partners/faguo.png';
import bashLogo from '../../../assets/partners/bash.png';
import lePantalonLogo from '../../../assets/partners/lePantalon.logo.png';
import maisonLemoine from '../../../assets/partners/maison_lemoine.png';
import americanVintage from '../../../assets/partners/amv_logo.svg';
import sessun from '../../../assets/partners/sessun_logo.png';
import circle from '../../../assets/partners/circle_logo.webp';
import bonsoirs from '../../../assets/partners/bonsoirs_logo.png';
import { typeFormPartnersLink } from '../../partner/dataPartner';

type ContainerProps = {
  noPadding: boolean;
  noMargin: boolean;
};
const Container = styled.div<ContainerProps>`
  max-width: 1180px;
  width: 100%;
  ${(props) => props.noMargin ? '' : `margin: 60px ${frames.desktop};`}
  ${(props) => props.noPadding ? '' : 'padding: 60px 0;'}
  @media (max-width: ${tabletThresholdPixels}) {
    ${(props) => props.noMargin ? `margin: 0px ${frames.tablet};` : `margin: ${margins.m14} ${frames.tablet};`}
    width: calc(100vw - (${frames.tablet}*2));
    padding: 0;
  }
  @media (max-width: ${mobileThresholdPixels}) {
    ${(props) => props.noMargin ? `margin: 0px ${frames.mobile};` : `margin: ${margins.m11} ${frames.mobile};`}
    width: calc(100vw - (${frames.mobile}*2));
  }
`;

const Text = styled.p`
  color: ${colors.grey600};
  font-size: 26px;
  margin: ${margins.m1} 0 ${margins.m6};
  line-height: 150%;
  @media (max-width: ${mobileThresholdPixels}) {
    font-size: 16px;
    margin: ${margins.m1} 0 ${margins.m3};
  }
`;

const OurPartnersContainer = styled.div`
  overflow: hidden;
  width: 100%;
  @media (max-width: ${mobileThresholdPixels}) {
    margin: ${margins.m3} 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const driftAnimation = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
`;

const Img = styled.img`
  max-width: 300px;
  max-height: 100px;
  height: ${(props) => props.height || 'auto'};
  @media (max-width: ${mobileThresholdPixels}) {
    width: 100px;
  }
`;

const InfiniteWrapper = styled.div`
  margin: 32px 0;
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 50px;
  animation: ${driftAnimation} 25s linear infinite;
  @media (max-width: ${mobileThresholdPixels}) {
    gap: 20px;
    margin: 0;
  }
`;

const ourPartnersData = [
  {
    key: 'bash',
    caption: 'Bash, retouches Paris',
    img: bashLogo,
  },
  {
    key: 'faguo',
    caption: 'Faguo, retoucherie avec des couturières',
    img: faguoLogo,
  },
  {
    key: 'tara_jarmon',
    caption: 'couturière Toulouse',
    img: taraJarmonLogo,
  },
  {
    key: 'madura',
    caption: 'Madura, retouches d’ourlets de rideaux',
    img: maduraLogo,
    height: '50px',
    mobileHeight: '30px',
  },
  {
    key: 'maison_lemoine',
    caption: 'Maison Lemoine, retouches d’ourlets de rideaux',
    img: maisonLemoine,
    height: '90px',
    mobileHeight: '60px',
  },
  {
    key: 'asphalte',
    caption: 'Asphalte, retouches Paris',
    img: asphalteLogo,
  },
  {
    key: 'soeur',
    caption: 'Soeur couturière costume',
    img: logoSoeur,
  },
  {
    key: '5ASec',
    caption: '5ASec couturière costume',
    img: logo5ASec,
  },
  {
    key: 'maria_de_la_orden',
    caption: 'Maria de la Orden couturière Paris',
    img: mariaDeLaOrdenLogo,
    height: '30px',
  },
  {
    key: 'le_pantalon',
    caption: 'Le Pantalon, la référence du pantalon',
    img: lePantalonLogo,
  },
  {
    key: 'american_vintage',
    caption: 'American Vintage, authentic fashion for all the family',
    img: americanVintage,
  },
  {
    key: 'sessun',
    caption: 'American Vintage, authentic fashion for all the family',
    img: sessun,
  },
  {
    key: 'circle',
    caption: 'American Vintage, authentic fashion for all the family',
    img: circle,
  },
  {
    key: 'bonsoirs',
    caption: 'American Vintage, authentic fashion for all the family',
    img: bonsoirs,
  },
];

type OurPartnersBlockProps = {
  context: {
    isMobile: boolean;
  };
  isLandingPage: boolean;
  isWhyBecomePartnerPage: boolean;
};

function OurPartnersBlock({
  context: { isMobile }, isLandingPage, isWhyBecomePartnerPage,
} : OurPartnersBlockProps) {
  const { t } = useTranslation();
  const repeatedData = Array.from({ length: 2 }, (_, index) => ourPartnersData.map((partner) => ({ ...partner, key: `${partner.key}_${index + 1}` }))).flat();
  return (
    <Container noPadding={isWhyBecomePartnerPage} noMargin={isWhyBecomePartnerPage}>
      {!isWhyBecomePartnerPage && <Title>{t('Nos partenaires')}</Title>}
      {!isWhyBecomePartnerPage && <Text>{t('Ensemble, nous prenons soin de vos produits')}</Text>}
      <OurPartnersContainer>
        <InfiniteWrapper>
          {repeatedData.map((partner) => (
            <Img src={partner.img} alt={partner.caption} key={partner.key} height={isMobile ? partner.mobileHeight : partner.height} />
          ))}
        </InfiniteWrapper>
      </OurPartnersContainer>

      {(!isWhyBecomePartnerPage && !isLandingPage) && (
        <Button transparent onClick={() => navigate(typeFormPartnersLink)}>
          {t('Devenir Partenaire')}
        </Button>
      )}
      {isLandingPage && (
        <CTALink
          fromInteraction="LandingPage"
        >
          <Button transparent>{t('Confier mes articles')}</Button>
        </CTALink>
      )}
    </Container>
  );
}

export default withAppContext(OurPartnersBlock);
